/**
 * Formatting helpers for insight drill-down views. These turn raw payload
 * numbers and internal field names into short, human-readable strings so the
 * UI can lead with the delta ("+766%", "133 → 18 TEU") instead of dumping
 * technical keys like "pct_change" or "prior_teu".
 */
import { splitLaneKey } from "@/lib/formatPort";
import { formatUsd } from "@/types/booking";

export type Rec = Record<string, unknown>;

/** Drop a trailing ".0" and round to one decimal for display. */
export function formatNum(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/** Title-case a snake_case key as a last-resort label. */
export function humanize(key: string): string {
  const cleaned = key.replace(/_/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** Naive pluralization: "1 opportunity" / "3 opportunities". */
export function plural(count: number, noun: string): string {
  if (count === 1) return `1 ${noun}`;
  const suffix = noun.endsWith("y") ? `${noun.slice(0, -1)}ies` : `${noun}s`;
  return `${count} ${suffix}`;
}

/** A signed percentage delta with a red/green class based on direction. */
export function formatDeltaPct(value: number): { text: string; className: string } {
  const rounded = Math.round(value);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "\u2212" : "";
  return {
    text: `${sign}${Math.abs(rounded)}%`,
    className: rounded < 0 ? "text-rose-500" : "text-emerald-500",
  };
}

/** "133 → 18 TEU" style before/after string. */
export function formatBeforeAfter(prior: number, current: number, unit: string): string {
  const u = unit ? ` ${unit}` : "";
  return `${formatNum(prior)} \u2192 ${formatNum(current)}${u}`;
}

/** Shared scale for a set of comparison bars (avoids a zero-width track). */
export function compareScale(values: number[]): number {
  const max = values.reduce((high, value) => Math.max(high, value), 0);
  return max > 0 ? max : 1;
}

/** Width of a comparison bar as a percentage of `max`. */
export function barWidthPct(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, (value / max) * 100));
}

/** Prior/current TEU pair on a list row, when the payload carries one. */
export function rowCompare(row: Rec): {
  prior: number;
  current: number;
  unit: string;
  deltaPct?: number;
} | null {
  const num = (k: string) => (typeof row[k] === "number" ? (row[k] as number) : undefined);
  const prior = num("prior_teu");
  const current = num("current_teu");
  if (prior === undefined || current === undefined) return null;
  return {
    prior,
    current,
    unit: "TEU",
    deltaPct: num("pct_change") ?? num("teu_pct_change"),
  };
}

/** Friendly labels for known metric keys; falls back to humanize(). */
const METRIC_LABELS: Record<string, string> = {
  capture_rate_pct: "Capture rate",
  detected: "Detected",
  actionable: "Actionable",
  acted_upon: "Acted upon",
  opened_count: "Opened",
  opened_teu: "Opened TEU",
  prior_count: "Prior period",
  rolled_count: "Rolled",
  delayed_count: "Delayed",
  active_bookings: "Active bookings",
  rolled_rate_pct: "Rolled share",
  delayed_rate_pct: "Delayed share",
  unrealized_savings_usd: "Unrealized savings",
  opportunities_detected: "Opportunities",
  actionable_count: "Actionable",
  acted_upon_count: "Acted upon",
  stale_count: "Stale",
  stale_pct: "Stale share",
  unmatched_count: "Unmatched",
  unmatched_pct: "Unmatched share",
  blocked_savings_usd: "Blocked savings",
  blocked_count: "Blocked",
};

export function pickMetricLabel(key: string): string {
  return METRIC_LABELS[key] ?? humanize(key);
}

/** Format a numeric metric based on naming conventions in the payloads. */
export function formatMetricValue(key: string, value: number): string {
  const k = key.toLowerCase();
  if (k.includes("usd") || k.includes("savings")) return formatUsd(value);
  if (k.includes("frt") || k.includes("preservation")) return formatUsd(value);
  if (k.endsWith("_pct") || k.includes("rate_pct") || k.includes("utilization")) {
    return `${Math.round(value)}%`;
  }
  if (k.includes("teu")) return `${formatNum(value)} TEU`;
  return formatNum(value);
}

/** Whether a metric key is meaningful enough to show in the generic KPI grid. */
export function isFriendlyMetricKey(key: string): boolean {
  if (key in METRIC_LABELS) return true;
  const k = key.toLowerCase();
  return (
    k.endsWith("_pct") ||
    k.endsWith("_count") ||
    k.endsWith("_usd") ||
    k.includes("teu") ||
    k.includes("rate")
  );
}

export type RowLabelKind =
  | "consignee"
  | "sales_person"
  | "carrier"
  | "lane"
  | "state"
  | "agent"
  | "opened_by"
  | "source_type"
  | "rule"
  | "generic";

const LABEL_FIELDS: [string, RowLabelKind][] = [
  ["consignee", "consignee"],
  ["sales_person", "sales_person"],
  ["carrier", "carrier"],
  ["source_type", "source_type"],
  ["lane_key", "lane"],
  ["lane", "lane"],
  ["state", "state"],
  ["agent", "agent"],
  ["opened_by", "opened_by"],
  ["rule", "rule"],
  ["label", "generic"],
  ["source", "generic"],
];

/** The primary label for a list row and what kind of entity it is. */
export function rowLabel(row: Rec): { text: string; kind: RowLabelKind } {
  for (const [field, kind] of LABEL_FIELDS) {
    const v = row[field];
    if (typeof v === "string" && v.length > 0) {
      if (kind === "generic" && splitLaneKey(v)) {
        return { text: v, kind: "lane" };
      }
      return { text: v, kind };
    }
  }
  return { text: "\u2014", kind: "generic" };
}

export interface RowSummary {
  primary: string;
  primaryClassName?: string;
  subtitle?: string;
  share?: number;
  ratio?: { value: number; total: number };
}

/**
 * Interpret an arbitrary insight list row into a single delta-first summary,
 * used by the generic fallback so it never enumerates every numeric key.
 */
export function rowSummary(row: Rec): RowSummary {
  const num = (k: string) => (typeof row[k] === "number" ? (row[k] as number) : undefined);

  const pct = num("pct_change") ?? num("teu_pct_change");
  if (pct !== undefined) {
    const delta = formatDeltaPct(pct);
    const parts: string[] = [];
    const priorTeu = num("prior_teu");
    const currentTeu = num("current_teu");
    if (priorTeu !== undefined && currentTeu !== undefined) {
      parts.push(formatBeforeAfter(priorTeu, currentTeu, "TEU"));
    }
    const priorBookings = num("prior_bookings");
    const currentBookings = num("current_bookings");
    if (priorBookings !== undefined && currentBookings !== undefined) {
      parts.push(`${priorBookings} \u2192 ${currentBookings} bookings`);
    }
    return {
      primary: delta.text,
      primaryClassName: delta.className,
      subtitle: parts.join(" \u00b7 ") || undefined,
    };
  }

  const savings = num("unrealized_savings_usd") ?? num("blocked_savings_usd");
  if (savings !== undefined) {
    const opp = num("opportunity_count");
    return {
      primary: formatUsd(savings),
      subtitle: opp !== undefined ? plural(opp, "opportunity") : undefined,
      share: savings,
    };
  }

  const rolled = num("rolled");
  const delayed = num("delayed");
  if (rolled !== undefined || delayed !== undefined) {
    return { primary: `${rolled ?? 0} rolled \u00b7 ${delayed ?? 0} delayed` };
  }

  const detected = num("detected");
  const acted = num("acted");
  if (detected !== undefined) {
    return {
      primary: `${acted ?? 0}/${detected} acted`,
      ratio: { value: acted ?? 0, total: detected },
    };
  }

  const count = num("count");
  if (count !== undefined) {
    const teu = num("teu");
    return {
      primary: String(count),
      subtitle: teu ? `${formatNum(teu)} TEU` : undefined,
      share: count,
    };
  }

  const unmatched = num("unmatched_teu");
  if (unmatched !== undefined) {
    return { primary: `${formatNum(unmatched)} TEU unmatched` };
  }

  for (const [k, v] of Object.entries(row)) {
    if (typeof v === "number") return { primary: formatMetricValue(k, v) };
  }
  return { primary: "" };
}
